import { useState } from 'react'
import { appUrl } from '@astro/config'
import { SectionCard } from './DetailBits'
import { formatRate } from './bookingDetailsData'
import { suggestionPrice } from './useSuggestProducts'
import { useBookingSuggestions } from './useBookingSuggestions'

/**
 * Seeker-side "Suggested Remedies" — the read-only counterpart of the
 * astrologer's SuggestProductPanel. Shows the products the astrologer pinned
 * to this consultation, with a path straight to the shop cart (suggestions
 * are already cart rows on the customer's account, so there's nothing to
 * "add" — they just check out).
 *
 * Data: useBookingSuggestions (shared with the downloadable consultation
 * report) — polls while the session is live.
 */
export default function SuggestedRemediesSection({ booking, live = false }) {
  const { data: suggestions = [], isPending } = useBookingSuggestions(booking, { live })

  // The section only exists once there's something to show — no empty-state
  // card cluttering bookings that never got suggestions.
  if (isPending || !suggestions.length) return null

  return (
    <SectionCard title="Suggested Remedies" icon="💎">
      <p className="text-xs text-[#8B7355] -mt-2 mb-5">
        Your astrologer recommended these for you — they&rsquo;re already waiting in your shop cart.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {suggestions.map((row) => (
          <RemedyCard key={row.id} row={row} />
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-5 border-t border-dashed border-[#C8922A]/20">
        <p className="text-xs text-[#8B7355]">
          {suggestions.length} remed{suggestions.length === 1 ? 'y' : 'ies'} added to your cart by the astrologer.
        </p>
        <a
          href={appUrl('shop', '/checkout')}
          className="hero-btn-primary inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em]"
        >
          🛒 View Cart &amp; Order
        </a>
      </div>
    </SectionCard>
  )
}

function RemedyCard({ row }) {
  const product = row.product ?? {}
  const price = suggestionPrice(row)
  const qty = Number(row.quantity) || 1
  const href = product.slug ? appUrl('shop', `/products/${product.slug}`) : appUrl('shop', '/checkout')

  return (
    <a
      href={href}
      className="group flex rounded-2xl border border-[#C8922A]/15 bg-white hover:border-[#C8922A]/45 hover:shadow-md transition-all overflow-hidden"
    >
      <Thumb src={product.image || product.image_url} alt={product.name} />
      <div className="min-w-0 grow p-3.5 flex flex-col">
        <p className="text-sm font-semibold text-[#1C140A] leading-snug line-clamp-2">
          {product.name || `Product #${row.product_id}`}
        </p>
        <p className="text-[10px] text-[#8B7355] mt-1">Qty {qty}</p>
        <div className="flex items-end justify-between gap-3 mt-auto pt-2">
          <p className="font-cinzel text-lg text-[#1C140A] leading-none">
            {price != null ? formatRate(price) : '—'}
          </p>
          <span className="text-[9px] font-bold uppercase tracking-widest text-[#C8922A] group-hover:underline">
            View →
          </span>
        </div>
      </div>
    </a>
  )
}

/** Product image with the jewel placeholder when missing or dead (the admin
 * server currently 403s /storage/*, so catalog URLs can fail to load). */
function Thumb({ src, alt }) {
  const [failed, setFailed] = useState(false)
  return (
    <div className="w-24 sm:w-27 self-stretch shrink-0 bg-[#FDFBF7] border-r border-[#C8922A]/10 flex items-center justify-center overflow-hidden">
      {src && !failed ? (
        <img
          src={src}
          alt={alt || 'Product'}
          loading="lazy"
          onError={() => setFailed(true)}
          className="w-full h-full object-cover"
        />
      ) : (
        <span className="text-xl opacity-40" aria-hidden="true">💎</span>
      )}
    </div>
  )
}
